/**
 * Schema.org JSON-LD builders for Google reviews (Phase 2A / 2E scaffold).
 *
 * These builders consume GOOGLE_REVIEWS + GOOGLE_REVIEW_STATS so the numbers
 * shown in the UI always match the numbers Google's Rich Results Test sees.
 */

import {
  GOOGLE_REVIEWS,
  GOOGLE_REVIEW_STATS,
  type GoogleReview,
} from '@/constants/googleReviews';
import { COMPANY_INFO } from '@/constants';

const BUSINESS_ID = 'https://www.elevationelectricalfl.com/#business';

export const buildAggregateRating = () => ({
  '@context': 'https://schema.org',
  '@type': 'AggregateRating',
  itemReviewed: { '@id': BUSINESS_ID },
  ratingValue: GOOGLE_REVIEW_STATS.ratingValue.toFixed(1),
  reviewCount: String(GOOGLE_REVIEW_STATS.totalReviews),
  bestRating: String(GOOGLE_REVIEW_STATS.bestRating),
  worstRating: String(GOOGLE_REVIEW_STATS.worstRating),
});

export const buildReviewList = (reviews: GoogleReview[] = GOOGLE_REVIEWS) =>
  reviews.map((r) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    '@id': `${BUSINESS_ID}/review/${r.id}`,
    itemReviewed: {
      '@type': 'Electrician',
      name: COMPANY_INFO.name,
      '@id': BUSINESS_ID,
    },
    author: { '@type': 'Person', name: r.author },
    datePublished: r.datePublished,
    reviewBody: r.text,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: String(r.rating),
      bestRating: '5',
      worstRating: '1',
    },
    publisher: { '@type': 'Organization', name: 'Google' },
  }));

/**
 * Convenience: emit a single JSON array combining aggregate + reviews, which
 * the carousel injects server-side (inline in Helmet) so the rich-results
 * eligibility lands on the first byte.
 */
export const buildGoogleReviewsJsonLd = () => [
  buildAggregateRating(),
  ...buildReviewList(),
];
